import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../config";
import "./LoanFullStyle.css";
import { FaSearch, FaUserCircle } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ─── helpers ─── */
const safeFloat = (v) => { const n = parseFloat(v); return isFinite(n) ? n : 0; };
const safeInt   = (v) => { const n = parseInt(v);   return isFinite(n) ? n : 0; };
const fmtNum    = (n) => { if (!isFinite(n) || isNaN(n)) return "0"; return parseFloat(n.toFixed(2)).toString(); };

const LOAN_TYPES = [
  { value: "flat_interest",     label: "Flat Interest" },
  { value: "reducing_interest", label: "Reducing Interest" },
  { value: "flat_interest_emi", label: "Flat Interest EMI" },
];

const EMPTY_FORM = {
  date_of_loan: "", deduct_from: "", created_date: "",
  loan_type: "flat_interest", amount: "", interest_rate: "",
  no_of_installments: "", loan_account_no: "", perquisite_rate: "0", remarks: "",
};
const EMPTY_INSTALL = { monthly_installment: "0", principal_balance: "0", interest_balance: "0" };

/* ════════════════════════════════════════════════════════════ */
const LoanPage = () => {
/* ════════════════════════════════════════════════════════════ */

  const [search,           setSearch]           = useState("");
  const [results,          setResults]          = useState([]);
  const [employee,         setEmployee]         = useState(null);
  const [activeTab,        setActiveTab]        = useState("general");
  const [loanSchedule,     setLoanSchedule]     = useState([]);
  const [transLoading,     setTransLoading]     = useState(false);
  const [hasActiveLoan,    setHasActiveLoan]    = useState(false);
  const [loanStatus,       setLoanStatus]       = useState(null);
  const [completedDate,    setCompletedDate]    = useState("");
  const [formData,         setFormData]         = useState({ ...EMPTY_FORM });
  const [installData,      setInstallData]      = useState({ ...EMPTY_INSTALL });
  const [repaymentData,    setRepaymentData]    = useState(null);
  const [repayLoading,     setRepayLoading]     = useState(false);
  const [loanHistory,      setLoanHistory]      = useState([]);
  const [historyLoading,   setHistoryLoading]   = useState(false);
  const [topupAmount,      setTopupAmount]      = useState("");
  const [newLoanPeriod,    setNewLoanPeriod]    = useState("");
  const [newInterestRate,  setNewInterestRate]  = useState("");
  const [repaymentAction,  setRepaymentAction]  = useState("");
  const [precloseAmount,   setPrecloseAmount]   = useState("");

  const setField = (key, value) => setFormData(prev => ({ ...prev, [key]: value ?? "" }));

  const isLoanActive = repaymentData?.loan_details?.loan_status === 1;

  const fmtDates = (s) => {
    if (!s) return "-";
    const d = new Date(s);
    return isNaN(d.getTime())
      ? s
      : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  /* ── Completed date ── */
  useEffect(() => {
    const start  = formData.deduct_from || formData.date_of_loan;
    const months = safeInt(formData.no_of_installments);
    if (!start || months <= 0) { setCompletedDate(""); return; }
    let y, m;
    if (typeof start === "string" && start.length === 7) {
      [y, m] = start.split("-").map(Number);
    } else {
      const d = new Date(start);
      if (isNaN(d.getTime())) { setCompletedDate(""); return; }
      y = d.getFullYear(); m = d.getMonth() + 1;
    }
    const total  = y * 12 + (m - 1) + (months - 1);
    const cYear  = Math.floor(total / 12);
    const cMonth = (total % 12) + 1;
    setCompletedDate(`${cYear}-${String(cMonth).padStart(2, "0")}`);
  }, [formData.deduct_from, formData.date_of_loan, formData.no_of_installments]);

  const buildRepaymentRows = () => {
    if (
      !repaymentData?.loan_details ||
      !Array.isArray(repaymentData?.repayment_history)
    ) return [];

    const originalLoan      = safeFloat(repaymentData.loan_details.amount_backup);
    const overallLoan       = safeFloat(repaymentData.loan_details.loan_amount);
    const totalInstallments = safeInt(repaymentData.loan_details.total_installments);

    const history = [...repaymentData.repayment_history].sort(
      (a, b) =>
        new Date(a.deduct_month + "-01") -
        new Date(b.deduct_month + "-01")
    );

    return history.map((row, index) => {
      const paidTillNow = history
        .slice(0, index + 1)
        .reduce((sum, r) => sum + safeFloat(r.deduct_amount), 0);

      const openingBalance = overallLoan - paidTillNow;

      return {
        sr: index + 1,
        effective_from:      row.deduct_month,
        opening_balance:     openingBalance,
        topup_amount:        overallLoan - originalLoan,
        loan_amount:         originalLoan,
        overall_loan_amount: overallLoan,
        interest_rate:       repaymentData.loan_details.interest_rate,
        remaining_period:    totalInstallments - (index + 1),
      };
    });
  };

  const handleTopupSave = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/loan/update-topup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          employee_id:       employee.user_id,
          topup_amount:      topupAmount,
          new_period:        newLoanPeriod,
          new_interest_rate: newInterestRate,
          loan_status:       repaymentAction,
          preclose_amount:   precloseAmount || 0
        })
      });

      const data = await res.json();

      if (data.status) {
        toast.success("Loan Updated Successfully");
        setTopupAmount("");
        setNewLoanPeriod("");
        setNewInterestRate("");
        setPrecloseAmount("");
        setRepaymentAction("");

        const statusRes  = await fetch(`${API_BASE_URL}/loan/status/${employee.user_id}`);
        const statusData = await statusRes.json();
        setLoanStatus(statusData.loan_status);
        setHasActiveLoan(statusData.loan_status === 1 || statusData.loan_status === 2);

        handleTabChange("repayments");
      } else {
        toast.error(data.message);
      }
    } catch {
      toast.error("Server Error");
    }
  };

  const repaymentRows = buildRepaymentRows();

  /* ── Installment calculation ── */
  useEffect(() => {
    const P    = safeFloat(formData.amount);
    const rate = safeFloat(formData.interest_rate);
    const N    = safeInt(formData.no_of_installments);
    const type = formData.loan_type || "flat_interest";
    if (P <= 0 || N <= 0) {
      setInstallData({ monthly_installment:"0", principal_balance: P>0 ? fmtNum(P):"0", interest_balance:"0" });
      return;
    }
    let emi = 0, interest = 0;
    if (type === "reducing_interest") {
      const R = rate / 12 / 100;
      if (R === 0) { emi = P / N; }
      else { const pow = Math.pow(1+R, N); emi = (P*R*pow)/(pow-1); interest = emi*N - P; }
    } else {
      interest = P * (rate/100) * (N/12);
      emi      = (P + interest) / N;
    }
    setInstallData({
      monthly_installment: fmtNum(isFinite(emi)      ? emi      : 0),
      principal_balance:   fmtNum(P),
      interest_balance:    fmtNum(isFinite(interest) ? interest : 0),
    });
  }, [formData.amount, formData.interest_rate, formData.no_of_installments, formData.loan_type]);

  /* ── Tab / fetch ── */
  const handleTabChange = async (tab) => {
    setActiveTab(tab);

    if (tab === "details" && employee) {
      setTransLoading(true);
      try {
        const res  = await fetch(`${API_BASE_URL}/loan/transactions/${employee.user_id}`);
        const data = await res.json();
        setLoanSchedule(Array.isArray(data) ? data : []);
      } catch { setLoanSchedule([]); }
      finally  { setTransLoading(false); }
    }

    if (tab === "repayments" && employee) {
      setRepayLoading(true);
      try {
        const res  = await fetch(`${API_BASE_URL}/loan/repayment-details/${employee.user_id}`);
        const data = await res.json();

        if (data.status) {
          setRepaymentData(data);
          if (data.loan_details && data.loan_details.loan_status) {
            setRepaymentAction(data.loan_details.loan_status.toString());
          }
          if (data.loan_details.preclose_amount) {
            setPrecloseAmount(data.loan_details.preclose_amount);
          }
        } else {
          setRepaymentData(null);
        }
      } catch {
        setRepaymentData(null);
      } finally {
        setRepayLoading(false);
      }
    }

    /* ── History fetch ── */
    if (tab === "history" && employee) {
      setHistoryLoading(true);
      try {
        const res  = await fetch(`${API_BASE_URL}/loan/history/${employee.user_id}`);
        const data = await res.json();
        setLoanHistory(Array.isArray(data) ? data : []);
      } catch {
        setLoanHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  /* ── Search ── */
  const handleSearch = async (value) => {
    setSearch(value);
    if (value.length < 2) { setResults([]); return; }
    try {
      const res  = await fetch(`${API_BASE_URL}/search-employees?search=${value}`);
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
    } catch { setResults([]); }
  };

  const selectEmployee = async (emp) => {
    try {
      const res  = await fetch(`${API_BASE_URL}/loan/status/${emp.user_id}`);
      const data = await res.json();

      setLoanStatus(data.loan_status);
      setHasActiveLoan(data.loan_status === 1 || data.loan_status === 2);

      setFormData({ ...EMPTY_FORM });
      setInstallData({ ...EMPTY_INSTALL });
      setCompletedDate("");
      setLoanSchedule([]);
      setRepaymentData(null);
      setTopupAmount("");
      setNewLoanPeriod("");
      setNewInterestRate("");

      setEmployee(emp);
      setResults([]);
      setSearch("");
      setActiveTab("general");
    } catch {
      toast.error("Error checking loan status");
    }
  };

  /* ── Save ── */
  const handleSave = async () => {
    if (!employee) { toast.error("Please select an employee"); return; }
    try {
      const res = await fetch(`${API_BASE_URL}/add-loan`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ employee_id: employee.user_id, ...formData, completed_date: completedDate, ...installData }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Loan Saved Successfully");
        setFormData({ ...EMPTY_FORM });
        setInstallData({ ...EMPTY_INSTALL });
        setCompletedDate("");

        const statusRes  = await fetch(`${API_BASE_URL}/loan/status/${employee.user_id}`);
        const statusData = await statusRes.json();
        setLoanStatus(statusData.loan_status);
        setHasActiveLoan(statusData.loan_status === 1 || statusData.loan_status === 2);
      } else {
        toast.error(data.message || "Error saving loan");
      }
    } catch { toast.error("Server Error"); }
  };

  /* ── Totals for schedule ── */
  const totals = loanSchedule.reduce(
    (a, r) => ({
      principal:          a.principal          + safeFloat(r.principal),
      interest:           a.interest           + safeFloat(r.interest),
      charges:            a.charges            + safeFloat(r.charges),
      installment_amount: a.installment_amount + safeFloat(r.installment_amount),
    }),
    { principal:0, interest:0, charges:0, installment_amount:0 }
  );

  const fmtRs   = (v) => Number(safeFloat(v).toFixed(2)).toLocaleString("en-IN", { minimumFractionDigits:2 });
  const fmtDate = (s) => {
    if (!s) return "-";
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });
  };
  const completedDisplay = completedDate
    ? (() => { const d = new Date(completedDate+"-01"); return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB",{month:"short",year:"numeric"}); })()
    : "";
  const selectedLoanType = LOAN_TYPES.find(o => o.value === formData.loan_type) || LOAN_TYPES[0];

  useEffect(() => {
    if (repaymentAction === "3" && repaymentRows.length > 0) {
      const openingBalance = repaymentRows[repaymentRows.length - 1].opening_balance;
      setPrecloseAmount(openingBalance.toString());
    }
  }, [repaymentAction, repaymentRows]);

  /* ════════════════ RENDER ════════════════ */
  return (
    <div className="grey-container">
      <ToastContainer position="bottom-right" />

      {!employee ? (
        <div className="grey-search-card">
          <div className="grey-search-left">
            <h5>Employee Loan Details</h5>
            <div className="grey-search-box">
              <FaUserCircle className="left-icon" />
              <input type="text" placeholder="Search by Emp No/ Name" value={search}
                onChange={(e) => handleSearch(e.target.value)} />
              <FaSearch className="right-icon" />
              {results.length > 0 && (
                <div className="search-dropdown">
                  {results.map((emp) => (
                    <div key={emp.employee_id} className="dropdown-item" onClick={() => selectEmployee(emp)}>
                      {emp.employee_id} - {emp.first_name} {emp.last_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="employee-bar">
            <div className="employee-chip">
              <FaUserCircle />
              <div>
                <strong>{employee.first_name} {employee.last_name}</strong>
                <div className="emp-id">#{employee.employee_id}</div>
              </div>
              <span
                className="close-chip"
                onClick={() => {
                  setEmployee(null);
                  setFormData({ ...EMPTY_FORM });
                  setInstallData({ ...EMPTY_INSTALL });
                  setCompletedDate("");
                  setLoanSchedule([]);
                  setRepaymentData(null);
                  setTopupAmount("");
                  setNewLoanPeriod("");
                  setNewInterestRate("");
                  setLoanStatus(null);
                }}
              >
                ×
              </span>
            </div>
          </div>

          <div className="loan-layout">
            <div className="loan-left">
              <div className="loan-title">LOAN</div>
              <ul>
                <li
                  className={activeTab === "general" ? "active" : ""}
                  onClick={() => handleTabChange("general")}
                  style={{ cursor: "pointer" }}
                >
                  General
                </li>

                <li className={activeTab === "details" ? "active" : ""}
                    onClick={() => handleTabChange("details")}
                    style={{ cursor: "pointer" }}>
                  Loan Details
                </li>

                <li className={activeTab === "repayments" ? "active" : ""}
                    onClick={() => handleTabChange("repayments")}
                    style={{ cursor: "pointer" }}>
                  Loan Repayments
                </li>

                <li
                  className={activeTab === "history" ? "active" : ""}
                  onClick={() => handleTabChange("history")}
                  style={{ cursor: "pointer" }}
                >
                  Loan History
                </li>
              </ul>
            </div>

            <div className="loan-right">
              {/* TABS */}
              <div className="loan-tabs">
                {[["general","General"],["details","Loan Details"],["repayments","Loan Repayments"],["history","Loan History"]].map(([k,l]) => (
                  <span key={k} className={activeTab===k?"active":""} style={{ cursor:"pointer" }}
                    onClick={() => handleTabChange(k)}>{l}</span>
                ))}
              </div>

              {/* ══ GENERAL ══ */}
              {activeTab === "general" && (
                <>
                  <div className="section-title">Loan Details</div>

                  {hasActiveLoan && (
                    <div style={{
                      background: "#fff3cd",
                      padding: "10px 15px",
                      border: "1px solid #ffeeba",
                      color: "#856404",
                      marginBottom: "15px",
                      borderRadius: "4px"
                    }}>
                      {loanStatus === 2
                        ? "Loan is on No Deduction status. Editing is disabled."
                        : "Loan Already Exists...."}
                    </div>
                  )}

                  <div className="form-grid">
                    {/* Date of Loan */}
                    <div>
                      <label>Date of Loan</label>
                      <DatePicker
                        selected={formData.date_of_loan ? new Date(formData.date_of_loan) : null}
                        onChange={(d) => setField("date_of_loan", d ? d.toLocaleDateString("en-CA") : "")}
                        className="form-control required-input"
                        disabled={hasActiveLoan}
                      />
                    </div>

                    {/* Amount */}
                    <div>
                      <label>Amount</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control required-input"
                        value={formData.amount}
                        onChange={(e) => setField("amount", e.target.value)}
                        disabled={hasActiveLoan}
                      />
                    </div>

                    {/* Deduct From */}
                    <div>
                      <label>Deduct From</label>
                      <DatePicker
                        selected={formData.deduct_from ? new Date(formData.deduct_from + "-01") : null}
                        onChange={(d) =>
                          setField(
                            "deduct_from",
                            d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : ""
                          )
                        }
                        dateFormat="MM/yyyy"
                        showMonthYearPicker
                        className="form-control required-input"
                        minDate={new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
                        disabled={hasActiveLoan}
                      />
                    </div>

                    {/* Interest Rate */}
                    <div>
                      <label>Interest Rate (%p.a)</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control required-input"
                        value={formData.interest_rate}
                        onChange={(e) => setField("interest_rate", e.target.value)}
                        disabled={hasActiveLoan}
                      />
                    </div>

                    {/* Created Date */}
                    <div>
                      <label>Created Date</label>
                      <DatePicker
                        selected={formData.created_date ? new Date(formData.created_date) : new Date()}
                        onChange={(d) =>
                          setField("created_date", d ? d.toLocaleDateString("en-CA") : "")
                        }
                        className="form-control"
                        minDate={new Date()}
                        maxDate={new Date()}
                        disabled={hasActiveLoan}
                      />
                    </div>

                    {/* Installments */}
                    <div>
                      <label>No of Installments (In Months)</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control required-input"
                        value={formData.no_of_installments}
                        onChange={(e) => setField("no_of_installments", e.target.value)}
                        disabled={hasActiveLoan}
                      />
                    </div>

                    {/* Loan Type */}
                    <div>
                      <label>Loan Type</label>
                      <Select
                        options={LOAN_TYPES}
                        value={selectedLoanType}
                        isSearchable={false}
                        isDisabled={hasActiveLoan}
                        onChange={(s) => setField("loan_type", s ? s.value : "flat_interest")}
                      />
                    </div>

                    {/* Loan Account No */}
                    <div>
                      <label>Loan Account No</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.loan_account_no}
                        onChange={(e) => setField("loan_account_no", e.target.value)}
                        disabled={hasActiveLoan}
                      />
                    </div>
                  </div>

                  {/* Installment Details */}
                  <div className="section-title">Installment Details</div>
                  <div className="installment-row">
                    <div className="installment-field">
                      <label>Monthly Installment</label>
                      <input type="text" value={installData.monthly_installment} readOnly />
                    </div>
                    <div className="installment-field">
                      <label>Principal Balance</label>
                      <input type="text" value={installData.principal_balance} readOnly />
                    </div>
                    <div className="installment-field">
                      <label>Interest Balance</label>
                      <input type="text" value={installData.interest_balance} readOnly />
                    </div>
                  </div>

                  {/* Other Information */}
                  <div className="section-title">Other Information</div>
                  <div className="other-info-grid">
                    <div className="checkbox-field">
                      <label>
                        <input type="checkbox" disabled={hasActiveLoan} /> Demand Promissory Note
                      </label>
                    </div>

                    <div className="checkbox-field">
                      <label>
                        <input type="checkbox" disabled={hasActiveLoan} /> Loan Completed
                      </label>
                    </div>

                    <div>
                      <label>Perquisite Rate</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.perquisite_rate}
                        onChange={(e) => setField("perquisite_rate", e.target.value)}
                        disabled={hasActiveLoan}
                      />
                    </div>

                    <div>
                      <label>Completed Date</label>
                      <input
                        type="text"
                        className="form-control"
                        value={completedDisplay}
                        readOnly
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="loan-buttons">
                    {!hasActiveLoan && (
                      <button className="btn btn-primary mt-4" onClick={handleSave}>
                        Save
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* ══ LOAN DETAILS ══ */}
              {activeTab === "details" && (
                <div style={{ marginTop: "16px" }}>
                  <div style={styles.stmtHeader}>
                    <h3 style={styles.stmtTitle}>Loan Schedule Statement</h3>
                    <p style={styles.stmtSub}></p>
                  </div>

                  {transLoading ? (
                    <div style={{ textAlign:"center", padding:"50px", color:"#888" }}>
                      <div style={styles.spinner} />
                      Loading schedule…
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={styles.table}>
                        <thead>
                          <tr style={styles.thead}>
                            <th style={styles.th}>S.no</th>
                            <th style={styles.th}>Due Date</th>
                            <th style={{ ...styles.th, textAlign:"right" }}>Principal</th>
                            <th style={{ ...styles.th, textAlign:"right" }}>Interest (₹)</th>
                            <th style={{ ...styles.th, textAlign:"right" }}>Charges (₹)</th>
                            <th style={{ ...styles.th, textAlign:"right" }}>Installment Amount (₹)</th>
                            <th style={{ ...styles.th, textAlign:"right" }}>Unpaid Installment (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loanSchedule.length === 0 ? (
                            <tr>
                              <td colSpan={7} style={{ textAlign:"center", padding:"40px", color:"#888", fontSize:"14px" }}>
                                No loan schedule found. Please ensure the employee has an active loan.
                              </td>
                            </tr>
                          ) : (
                            <>
                              {loanSchedule.map((row, i) => (
                                <tr key={i} style={{ background: i%2===0 ? "#fff" : "#f8f9fa", borderBottom:"1px solid #e9ecef" }}>
                                  <td style={styles.td}>{row.sr_no}</td>
                                  <td style={styles.td}>{fmtDate(row.due_date)}</td>
                                  <td style={{ ...styles.td, textAlign:"right" }}>{fmtRs(row.principal)}</td>
                                  <td style={{ ...styles.td, textAlign:"right" }}>{fmtRs(row.interest)}</td>
                                  <td style={{ ...styles.td, textAlign:"right" }}>{fmtRs(row.charges)}</td>
                                  <td style={{ ...styles.td, textAlign:"right" }}>{fmtRs(row.installment_amount)}</td>
                                  <td style={{ ...styles.td, textAlign:"right" }}>{fmtRs(row.unpaid_installment)}</td>
                                </tr>
                              ))}

                              <tr style={styles.totalRow}>
                                <td style={{ ...styles.tdTotal }} colSpan={2}>Total</td>
                                <td style={{ ...styles.tdTotal, textAlign:"right" }}>{fmtRs(totals.principal)}</td>
                                <td style={{ ...styles.tdTotal, textAlign:"right" }}>{fmtRs(totals.interest)}</td>
                                <td style={{ ...styles.tdTotal, textAlign:"right" }}>{fmtRs(totals.charges)}</td>
                                <td style={{ ...styles.tdTotal, textAlign:"right" }}>{fmtRs(totals.installment_amount)}</td>
                                <td style={{ ...styles.tdTotal, textAlign:"right" }}>—</td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="loan-buttons" style={{ marginTop:"20px" }}>
                    <button className="btn btn-primary mt-4" onClick={() => handleTabChange("general")}>
                      Back
                    </button>
                  </div>
                </div>
              )}

              {/* ══ LOAN HISTORY ══ */}
              {activeTab === "history" && (
                <div style={{ marginTop: "20px" }}>
                  <div className="section-title">Loan History</div>

                  {historyLoading ? (
                    <p>Loading...</p>
                  ) : loanHistory.length === 0 ? (
                    <p>No previous loans found.</p>
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Loan Amount</th>
                          <th>Interest Rate</th>
                          <th>Total Installments</th>
                          <th>Status</th>
                          <th>Completed Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loanHistory.map((loan, i) => (
                          <tr key={i}>
                            <td>{loan.amount}</td>
                            <td>{loan.interest_rate}%</td>
                            <td>{loan.no_of_installments}</td>
                           <td>
  {loan.loan_status === 2
    ? "No Deduction"
    : loan.loan_status === 3
    ? "Pre Closed"
    : loan.loan_status === 4
    ? "Closed"
    : "-"}
</td>
                            <td>{loan.completed_date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* ══ REPAYMENTS ══ */}
              {activeTab === "repayments" && (
                <div style={{ marginTop: "20px" }}>
                  <div style={{
                    background: "#f2f4f7",
                    padding: "12px 15px",
                    border: "1px solid #dcdfe6",
                    marginBottom: "20px"
                  }}>
                    <h5 style={{ margin: 0 }}>Running Loan Details</h5>
                  </div>

                  {repaymentData && (
                    <>
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: "20px 60px"
                      }}>
                        <div>
                          <label>Loan Amount</label>
                          <input className="form-control"
                            value={repaymentData.loan_details.loan_amount}
                            readOnly
                          />
                        </div>

                        <div>
                          <label>Current Interest Rate(%p.a)</label>
                          <input className="form-control"
                            value={repaymentData.loan_details.interest_rate}
                            readOnly
                          />
                        </div>

                        <div>
                          <label>Loan Type</label>
                          <input className="form-control"
                            value={repaymentData.loan_details.loan_type}
                            readOnly
                          />
                        </div>

                        <div>
                          <label>Total Installments</label>
                          <input className="form-control"
                            value={repaymentData.loan_details.total_installments}
                            readOnly
                          />
                        </div>

                        <div>
                          <label>Created Date</label>
                          <input
                            className="form-control"
                            value={fmtDates(repaymentData.loan_details.created_at)}
                            readOnly
                          />
                        </div>

                        <div>
                          <label>Principal Balance</label>
                          <input className="form-control"
                            value={repaymentData.loan_details.principal_balance}
                            readOnly
                          />
                        </div>

                        <div>
                          <label>No. of Installments Paid</label>
                          <input className="form-control"
                            value={repaymentData.installments_paid}
                            readOnly
                          />
                        </div>

                        <div>
                          <label>Top Up Amount</label>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={topupAmount}
                            onChange={(e) => setTopupAmount(e.target.value)}
                          />
                        </div>

                        <div>
                          <label>New Loan Period</label>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={newLoanPeriod}
                            onChange={(e) => setNewLoanPeriod(e.target.value)}
                          />
                        </div>

                        <div>
                          <label>New Interest Rate</label>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={newInterestRate}
                            onChange={(e) => setNewInterestRate(e.target.value)}
                          />
                        </div>

                        {/* Repayment Action Dropdown */}
                        <div>
                          <label>Repayment Action</label>
                          <select
                            className="form-control"
                            value={repaymentAction}
                            onChange={(e) => setRepaymentAction(e.target.value)}
                          >
                            <option value="1" disabled={repaymentAction === "1"}>Deduction</option>
                            <option value="2" disabled={repaymentAction === "2"}>No Deduction</option>
                            <option value="3" disabled={repaymentAction === "3"}>Pre Close</option>
                          </select>
                        </div>

                        {/* Pre Close Amount */}
                        {repaymentAction === "3" && (
                          <div>
                            <label>Pre Close Amount</label>
                            <input
                              type="number"
                              min="0"
                              className="form-control"
                              readOnly
                              value={precloseAmount}
                              onChange={(e) => setPrecloseAmount(e.target.value)}
                            />
                          </div>
                        )}
                      </div>

                      {/* Buttons */}
                      <div style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "15px",
                        marginTop: "25px"
                      }}>
                        <button
                          className="btn btn-primary mt-4"
                          onClick={() => handleTabChange("general")}
                        >
                          Back
                        </button>

                        <button className="btn btn-primary mt-4" onClick={handleTopupSave}>
                          Save
                        </button>
                      </div>

                      {/* TABLE SECTION */}
                      <div style={{
                        marginTop: "30px",
                        borderTop: "1px solid #dcdfe6",
                        paddingTop: "20px"
                      }}>
                        <table style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "13px"
                        }}>
                          <thead style={{ background: "#f2f4f7" }}>
                            <tr>
                              {[
                                "Effective From",
                                "Opening Balance",
                                "Top Up Amount",
                                "Loan Amount",
                                "Overall Loan Amount",
                                "Interest Rate",
                                "Remaining Period"
                              ].map(col => (
                                <th key={col} style={{
                                  padding: "10px",
                                  border: "1px solid #e0e6ed",
                                  textAlign: "left",
                                  fontWeight: "600"
                                }}>
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          <tbody>
                            {repaymentRows.length === 0 ? (
                              <tr>
                                <td colSpan={8} style={{ textAlign: "center", padding: "25px", color: "#888" }}>
                                  No repayment history found.
                                </td>
                              </tr>
                            ) : (
                              repaymentRows.map((r) => (
                                <tr key={r.sr}>
                                  <td style={tdStyle}>{r.effective_from}</td>
                                  <td style={tdStyle}>{fmtRs(r.opening_balance)}</td>
                                  <td style={tdStyle}>{fmtRs(r.topup_amount)}</td>
                                  <td style={tdStyle}>{fmtRs(r.loan_amount)}</td>
                                  <td style={tdStyle}>{fmtRs(r.overall_loan_amount)}</td>
                                  <td style={tdStyle}>{r.interest_rate}</td>
                                  <td style={tdStyle}>{r.remaining_period}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

            </div>
          </div>
        </>
      )}
    </div>
  );
};

const thStyle = {
  padding: "10px",
  border: "1px solid #e2e8f0",
  textAlign: "left",
  fontWeight: "600",
  background: "#f1f5f9"
};

const tdStyle = {
  padding: "8px",
  border: "1px solid #e2e8f0"
};

/* ─── Styles ─── */
const styles = {
  stmtHeader: {
    textAlign: "center",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "2px solid #dee2e6",
  },
  stmtTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1a1a2e",
    margin: "0 0 4px",
    textDecoration: "underline",
  },
  stmtSub: {
    fontSize: "12px",
    color: "#6c757d",
    margin: 0,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    minWidth: "780px",
    border: "1px solid #dee2e6",
  },
  thead: {
    background: "#a1a4a7",
  },
  th: {
    padding: "11px 14px",
    color: "#181616",
    fontWeight: "600",
    fontSize: "12.5px",
    whiteSpace: "nowrap",
    borderRight: "1px solid #e9ecee",
    textAlign: "left",
  },
  td: {
    padding: "9px 14px",
    color: "#333",
    fontSize: "13px",
    whiteSpace: "nowrap",
    borderRight: "1px solid #e9ecef",
  },
  totalRow: {
    background: "#eaf2ff",
    borderTop: "2px solid #0e0f0f",
  },
  tdTotal: {
    padding: "10px 14px",
    fontWeight: "700",
    fontSize: "13px",
    color: "#1a1a2e",
    borderRight: "1px solid #cdd8e8",
  },
  spinner: {
    width: "28px",
    height: "28px",
    border: "3px solid #e0e0e0",
    borderTop: "3px solid #1a5276",
    borderRadius: "50%",
    margin: "0 auto 10px",
  },
};

export default LoanPage;